import React, { useState, useEffect } from 'react';
import { useAppTheme } from '../theme/AppThemeContext.jsx';
import { useParams } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import { resolveShareToken, getTasksForSharedView } from '../database/firebaseDatabase.js';
import { DayViewTimeline } from '../components/features/DayViewTimeline.jsx';

function toDateString(date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .split('T')[0];
}

function formatDateLabel(date) {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function SharedView() {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // 'loading' | 'ready' | 'expired' | 'error'
  const [ownerUid, setOwnerUid] = useState(null);
  const [ownerLabel, setOwnerLabel] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [taskLoading, setTaskLoading] = useState(false);

  const { tokens } = useAppTheme();
  const muted = 'rgb(133, 133, 133)';

  // Resolve the token on mount
  useEffect(() => {
    async function resolve() {
      try {
        const tokenDoc = await resolveShareToken(token);
        if (!tokenDoc || !tokenDoc.active) {
          setState('expired');
          return;
        }
        setOwnerUid(tokenDoc.ownerUid);
        setOwnerLabel(tokenDoc.label || 'Shared schedule');
        setState('ready');
      } catch (err) {
        console.error('Failed to resolve share token:', err);
        setState('error');
      }
    }
    resolve();
  }, [token]);

  // Fetch tasks whenever ownerUid or selectedDate changes
  useEffect(() => {
    if (!ownerUid) return;
    setTaskLoading(true);
    const dateStr = toDateString(selectedDate);
    getTasksForSharedView(ownerUid, dateStr)
      .then(rawTasks => {
        // Sort by start time
        const sorted = [...rawTasks].sort((a, b) => {
          const ta = a.startTime ? a.startTime.split(':').map(Number) : [0, 0];
          const tb = b.startTime ? b.startTime.split(':').map(Number) : [0, 0];
          return (ta[0] * 60 + ta[1]) - (tb[0] * 60 + tb[1]);
        });
        setTasks(sorted.map(t => ({ ...t, kind: t.kind || 'task' })));
      })
      .catch(err => console.error('Failed to load shared tasks:', err))
      .finally(() => setTaskLoading(false));
  }, [ownerUid, selectedDate]);

  const changeDate = (delta) => {
    setSelectedDate(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + delta);
      return d;
    });
  };

  if (state === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: tokens.primary }} />
      </Box>
    );
  }

  if (state === 'expired') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Link Expired</Typography>
        <Typography sx={{ color: muted }}>This share link is no longer active.</Typography>
      </Box>
    );
  }

  if (state === 'error') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Something went wrong</Typography>
        <Typography sx={{ color: muted }}>Unable to load the shared schedule.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'rgb(248, 248, 248)' }}>
      {/* View-only banner */}
      <Box
        sx={{
          background: tokens.primary,
          color: 'white',
          textAlign: 'center',
          py: 0.75,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.5,
        }}
      >
        VIEW ONLY — {ownerLabel}
      </Box>

      {/* Date navigation */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          px: 2,
          py: 1.5,
          background: 'white',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <Box
          component="button"
          onClick={() => changeDate(-1)}
          sx={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: 20,
            color: muted,
            lineHeight: 1,
            px: 1,
          }}
        >
          ‹
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 15 }}>
          {formatDateLabel(selectedDate)}
        </Typography>
        <Box
          component="button"
          onClick={() => changeDate(1)}
          sx={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: 20,
            color: muted,
            lineHeight: 1,
            px: 1,
          }}
        >
          ›
        </Box>
      </Box>

      {/* Task list */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {taskLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress size={24} sx={{ color: tokens.primary }} />
          </Box>
        ) : tasks.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography variant="h6" color="text.secondary">No tasks for this day.</Typography>
          </Box>
        ) : (
          <DayViewTimeline
            rows={tasks}
            containerBg="rgb(248, 248, 248)"
          />
        )}
      </Box>
    </Box>
  );
}
